import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

/** How many occupant names the refusal message lists before "+N more". */
const SAMPLE_LIMIT = 3;

/** Occupants whose name marks them as ours — used ONLY to escalate the refusal
 *  wording, never to decide whether to refuse. See the note on asymmetry below. */
const M8T_NAME_RE = /^m8t/i;

export type RgOccupancyState = "absent" | "empty" | "occupied";

export interface RgOccupancy {
  state: RgOccupancyState;
  /** Display sample, capped at SAMPLE_LIMIT. */
  found: string[];
  /** Full occupant count — `found.length` may be smaller. */
  total: number;
  /** Computed over the FULL list, before the sample is capped. */
  m8tShaped: boolean;
}

/**
 * Is the target resource group safe to install into?
 *
 * The signal is deliberately "does it hold ANY resource", not "does it hold an
 * m8t deployment". A guard keyed on our own tags or artifact names has already
 * failed live — a tag rename left older deployments carrying stale keys, so a
 * tag-keyed check would miss exactly the aging production-shaped groups this
 * exists to protect. The failure modes are asymmetric: a false negative is a
 * repeat of the identity-overwrite incident, while a false positive costs one
 * explicit flag.
 *
 * Fail-CLOSED: any ARM failure throws BOOTSTRAP_OCCUPANCY_UNVERIFIED. Installs
 * are rare and high-stakes, and a retry is cheap; failing open would reopen the
 * hole on every transient error.
 *
 * Known blind spot: `az resource list` does not return role assignments,
 * resource locks, or budgets — a group holding only those reads as empty.
 */
export async function checkTargetRgOccupancy(opts: {
  resourceGroup: string;
  subscriptionId: string;
}): Promise<RgOccupancy> {
  // `az group exists` answers true/false and does NOT fail on a missing group,
  // so a throw here genuinely means "could not verify" rather than "absent" —
  // no stderr parsing required to tell those apart.
  let existsRaw: string;
  try {
    existsRaw = await runAz([
      "group", "exists", "--name", opts.resourceGroup,
      "--subscription", opts.subscriptionId, "-o", "tsv",
    ]);
  } catch (cause) {
    throw unverified(opts.resourceGroup, cause);
  }

  const exists = existsRaw.trim().toLowerCase();
  if (exists === "false") return { state: "absent", found: [], total: 0, m8tShaped: false };
  if (exists !== "true") {
    throw unverified(opts.resourceGroup, new Error(`unexpected 'az group exists' output: ${exists}`));
  }

  let names: string[];
  try {
    const listRaw = await runAz([
      "resource", "list", "--resource-group", opts.resourceGroup,
      "--subscription", opts.subscriptionId, "--query", "[].name", "-o", "json",
    ]);
    names = JSON.parse(listRaw) as string[];
  } catch (cause) {
    throw unverified(opts.resourceGroup, cause);
  }
  if (!Array.isArray(names)) {
    throw unverified(opts.resourceGroup, new Error("resource listing was not an array"));
  }

  if (names.length === 0) return { state: "empty", found: [], total: 0, m8tShaped: false };

  return {
    state: "occupied",
    found: names.slice(0, SAMPLE_LIMIT),
    total: names.length,
    // Computed BEFORE the cap: an m8t-named resource sorting past the sample
    // must still escalate the wording.
    m8tShaped: names.some((n) => M8T_NAME_RE.test(n)),
  };
}

// `--reinstall-into` is consent to OBSERVED state: it is consulted only after
// a successful occupancy read that listed the occupants, so the founder is
// consenting to what they were shown. An unverified read shows nothing, so
// there is nothing to consent to yet — the flag must not appear in this
// message, or in any hint/doc reachable from this fail-closed path. Naming it
// here would teach a founder to pass it pre-emptively, and on a later run
// (once ARM answers) it would silently authorize an install neither of you
// has actually seen the occupants for. Do not re-add it.
function unverified(resourceGroup: string, cause: unknown): LocalCliError {
  return new LocalCliError({
    code: "BOOTSTRAP_OCCUPANCY_UNVERIFIED",
    message: `Could not verify whether resource group '${resourceGroup}' is empty.`,
    hint: "Check your connection and 'az login', then retry.",
    cause,
  });
}

/**
 * The refusal shown when the target holds resources.
 *
 * Occupant-agnostic by default: this check verifies OCCUPANCY, not m8t-ness, so
 * it must never claim to have found an m8t deployment it did not identify. The
 * stronger sentence is appended only when the occupant names say so. Keying on
 * our naming convention is banned in the gate but fine here, because a missed
 * convention in the message only softens a refusal that already happened.
 */
export function buildOccupiedRefusal(args: {
  resourceGroup: string;
  subscriptionId: string;
  location: string;
  occupancy: RgOccupancy;
}): string {
  const { resourceGroup: rg, subscriptionId, location, occupancy: o } = args;
  const hidden = o.total - o.found.length;
  const more = hidden > 0 ? ` (+${String(hidden)} more)` : "";
  const risk = o.m8tShaped
    ? "Installing into a non-empty resource group risks overwriting what's already there.\n" +
      "  This looks like an existing m8t deployment; installing here would overwrite its identity."
    : "Installing into a non-empty resource group risks overwriting what's already there.";

  return [
    "",
    "⛔ CANNOT PROCEED",
    `  Resource group '${rg}' in subscription ${subscriptionId} is not empty — it holds`,
    `  ${String(o.total)} resources, including: ${o.found.join(", ")}${more}.`,
    `  ${risk}`,
    "  remedy: pick an empty resource group —",
    `          m8t bootstrap launch --location ${location} --resource-group <new-rg>`,
    "          or, if you really mean to install into this one:",
    `          m8t bootstrap launch --location ${location} --resource-group ${rg} --reinstall-into ${rg}`,
    "  Nothing has been created.",
    "",
    "",
  ].join("\n");
}
